/**
 * userService — user management API calls routed through apiClient.
 *
 * All Authorization headers are injected automatically by apiClient's
 * request interceptor — no component or service should read authToken
 * from localStorage manually.
 */

import { apiClient } from "./apiService";
import { getSourceName, getUserProfile } from "./authService";
import { getApiSourceName, getClientEntry } from "../config/clientConfig";
import { normaliseUserList } from "../utils/userList";
import {
  fillUserEndpoint,
  resolveUserWriteEndpoints,
  userWriteUnsupportedError,
} from "./userEndpoints";

// Resolve the current session's source name — no static JSON fallback.
const effectiveSourceName = () =>
  getSourceName() || getUserProfile()?.client_source_name || "";

/**
 * The backend's own name for this client, which is not always the key the
 * session is stored under: Ergo's property line is `ergo_property` here and
 * `ergobalticproperty` to its API. Sending the key returns an empty list rather
 * than an error, so this distinction is worth keeping explicit.
 */
const backendSourceName = () => getApiSourceName() || effectiveSourceName();

/**
 * The user list, whose route and verb differ by client.
 *
 * Bajaj and Ergo motor: GET /client_user?client_source_name=…
 * Ergo property:        POST /useridList with source_name in a form body
 * Hegic:                GET /get_user — no query string at all, confirmed by
 *                        a captured request carrying only the Bearer token.
 *                        Same pattern as its other v2 reads (`/assessment_list/all`):
 *                        the token scopes the result, so there is nothing to
 *                        pass — `getUserEndpointUsesSourceName: false` in
 *                        hegic.json is what turns this off for just that client.
 *
 * All three come from the client's own file — `getUserEndpoint`,
 * `userListMethod`, `getUserEndpointUsesSourceName` — so a new client is a
 * config change. The reply is normalised to an array: property returns a
 * dict keyed "0","1",… while the others return a list, and no caller should
 * have to know which.
 */
export const getUsers = async () => {
  const client = getClientEntry(effectiveSourceName()) || {};
  const source = backendSourceName();
  const path = client.getUserEndpoint || "/client_user";
  const method = (client.userListMethod || "GET").toUpperCase();

  console.log(`[UserService] getUsers | ${method} ${path} | source:`, source);

  const response =
    method === "POST"
      ? await apiClient.post(path, new URLSearchParams({ source_name: source }).toString(), {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
        })
      : await apiClient.get(
          client.getUserEndpointUsesSourceName === false || path.includes("?")
            ? path
            : `${path}?client_source_name=${encodeURIComponent(source)}`,
        );

  return normaliseUserList(response.data);
};


/**
 * Create a user, on whichever route this client's backend actually has.
 *
 * Ergo's /client_user, Bajaj's and Hegic's /add_user — from the client's own
 * file, like the list endpoint. A client whose backend has no add route throws
 * before anything is sent rather than 404ing: see ./userEndpoints.
 *
 * The body shape is NOT one-size-fits-all, confirmed by captured requests:
 *
 *   Hegic (`client.userForm`):        access_level, name, email, password,
 *                                      role, permission (JSON array string).
 *   Ergo motor (`client.usesMotorAccessLevel`): client_id, full_name,
 *                                      client_source_name, motor_access_level,
 *                                      anc_access_level (always sent empty —
 *                                      this app has no UI for it), user_id.
 *                                      No `flag` at all; a new user's status
 *                                      is whatever the backend defaults to.
 *   Everyone else:                     the original Ergo-shaped body
 *                                      (client_id, full_name,
 *                                      client_source_name, access_level,
 *                                      user_id, flag), unverified beyond
 *                                      "POST, and it exists" — an
 *                                      unauthenticated write was not
 *                                      something to try against a live
 *                                      preprod backend.
 */
export const addClientUser = async (userData) => {
  const clientKey = effectiveSourceName();
  const client = getClientEntry(clientKey) || {};
  const { add: endpoint } = resolveUserWriteEndpoints(clientKey);
  if (!endpoint) throw userWriteUnsupportedError("add", clientKey);

  console.log(
    `[UserService] addClientUser | POST ${endpoint} | source:`,
    userData.client_source_name,
  );

  // client_source_name here is the backend's own name (Ergo: "ergobaltic"),
  // not the registry key clientKey/effectiveSourceName() resolves to ("ergo")
  // — same two-names distinction backendSourceName() exists for in getUsers()
  // above. Sending the registry key was confirmed live on Ergo motor's add-user.
  const body = client.userForm
    ? new URLSearchParams({
        access_level: userData.access_level ?? "0",
        name: userData.full_name,
        email: userData.user_id,
        password: userData.password,
        role: userData.role,
        permission: JSON.stringify(userData.permissions || []),
      })
    : client.usesMotorAccessLevel
      ? new URLSearchParams({
          client_id: userData.client_id,
          full_name: userData.full_name,
          client_source_name: userData.client_source_name || backendSourceName(),
          motor_access_level: userData.access_level,
          anc_access_level: "",
          user_id: userData.user_id,
        })
      : new URLSearchParams({
          client_id: userData.client_id,
          full_name: userData.full_name,
          client_source_name: userData.client_source_name || backendSourceName(),
          access_level: userData.access_level,
          user_id: userData.user_id,
          flag: userData.flag,
        });

  const response = await apiClient.post(endpoint, body.toString(), {
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
  });
  return response.data;
};

/**
 * Whether an email already belongs to a user on this client's backend.
 *
 * Only clients naming `emailCheckEndpoint` in their own file have this route
 * at all — every other client's config omits it, so this resolves to `false`
 * without firing a request and Add User stays exactly as it was for them.
 *
 * Hegic's is a GET with the email as a query param (`/email_check?email=…`).
 * Ergo motor's is a POST with it in a form body (`/emailcheck`, confirmed by
 * a captured request) — `emailCheckMethod` in the client's own file picks
 * which, defaulting to GET.
 */
export const checkEmailExists = async (email) => {
  const client = getClientEntry(effectiveSourceName()) || {};
  const endpoint = client.emailCheckEndpoint;
  if (!endpoint || !email) return false;

  const method = (client.emailCheckMethod || "GET").toUpperCase();
  console.log(`[UserService] checkEmailExists | ${method} ${endpoint} | email:`, email);

  const response =
    method === "POST"
      ? await apiClient.post(endpoint, new URLSearchParams({ email }).toString(), {
          headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
        })
      : await apiClient.get(endpoint, { params: { email } });

  return extractEmailExists(response.data);
};

/**
 * The real response body was never captured — only "0 or 1" was confirmed.
 * Reads a bare number/string as readily as an object wrapping one under a
 * plausible key, so whichever shape the real response turns out to be, this
 * does not need to change.
 */
const extractEmailExists = (data) => {
  if (typeof data === "number") return data === 1;
  if (typeof data === "string") return data.trim() === "1";
  if (data && typeof data === "object") {
    const value = data.exists ?? data.email_exists ?? data.status ?? data.result;
    return value === 1 || value === "1" || value === true;
  }
  return false;
};

/**
 * Update a user.
 *
 * Only Ergo's deployments have an edit route (PUT /client_user/<base64 id>/).
 * Bajaj's gateway and Hegic's host answer 404 to it and to every other name
 * tried, so their client files declare `editUserEndpoint: null` and this throws
 * without sending anything.
 *
 * Ergo motor and Ergo property share that route but NOT the same body, each
 * confirmed by a captured request:
 *
 *   property: keyValue only — full_name, user_id, access_level, flag.
 *   motor:    keyValue's access level is split into motor_access_level (this
 *             app's role picker) and anc_access_level (a line this app has no
 *             UI for — always sent back exactly as the list returned it, via
 *             userData.ancAccessLevel, never invented or blanked); the email
 *             is ALSO repeated as its own top-level `email_id` field, not
 *             just inside keyValue.
 *
 * `client.usesMotorAccessLevel` (ergo.json only) picks the second shape —
 * same key addClientUser reads for the same split on create.
 */
export const editClientUser = async (userId, userData) => {
  const clientKey = effectiveSourceName();
  const client = getClientEntry(clientKey) || {};
  const { edit: template } = resolveUserWriteEndpoints(clientKey);
  if (!template) throw userWriteUnsupportedError("edit", clientKey);

  const endpoint = fillUserEndpoint(template, userId);
  console.log(`[UserService] editClientUser | PUT ${endpoint} | userId:`, userId);

  const keyValue = client.usesMotorAccessLevel
    ? {
        full_name: userData.full_name,
        user_id: userData.user_id,
        motor_access_level: userData.access_level,
        anc_access_level: userData.ancAccessLevel ?? "",
        flag: userData.flag,
      }
    : {
        full_name: userData.full_name,
        user_id: userData.user_id,
        access_level: userData.access_level,
        flag: userData.flag,
      };

  const body = new URLSearchParams({ keyValue: JSON.stringify(keyValue) });
  if (client.usesMotorAccessLevel) {
    body.set("email_id", userData.user_id);
  }

  const response = await apiClient.put(endpoint, body.toString(), {
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
  });
  return response.data;
};

/**
 * Suspend or re-activate a user.
 *
 * Ergo property reuses its own general edit route for this — `PUT
 * /client_user/<id_b64>/` with `keyValue={"flag": 1}`, a *partial* body next
 * to the full one `editClientUser` sends, confirmed by a captured request.
 * `JSON.stringify` drops the undefined full_name/user_id/access_level this
 * function never sets, which is what produces that partial body without a
 * second code path.
 *
 * Hegic's is a genuinely different, narrower route (`PUT /edit_user/<id>/`)
 * confirmed the same way, and also confirmed to run the *opposite* flag
 * convention — the request was captured by clicking "Activate" on the one
 * suspended account in its own table, and it sent `flag: 0`. So neither the
 * route nor the active/inactive values can be assumed from one client to the
 * next: `userStatusEndpoint`, `activeFlagValue` and `inactiveFlagValue` in
 * each client's own file name all three (Ergo's own 1/2 are the default,
 * matching editClientUser's existing status dropdown; Hegic overrides every
 * one of them). toUserRow reads the list back the same way
 * (src/utils/userRoles.js).
 */
export const setUserStatus = async (userId, active) => {
  const clientKey = effectiveSourceName();
  const client = getClientEntry(clientKey) || {};
  const { status: template } = resolveUserWriteEndpoints(clientKey);
  if (!template) throw userWriteUnsupportedError("status", clientKey);

  const activeFlagValue = client.activeFlagValue ?? 1;
  const inactiveFlagValue = client.inactiveFlagValue ?? 2;
  const flag = active ? activeFlagValue : inactiveFlagValue;

  const endpoint = fillUserEndpoint(template, userId);
  console.log(`[UserService] setUserStatus | PUT ${endpoint} | active:`, active, "flag:", flag);
  const response = await apiClient.put(
    endpoint,
    new URLSearchParams({ keyValue: JSON.stringify({ flag }) }).toString(),
    { headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" } },
  );
  return response.data;
};
